import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { getProfile, login as apiLogin, logout as apiLogout } from '../services/emailApi';

const AuthContext = createContext(null);

const ACCOUNTS_KEY = 'jubly_linked_accounts';

function loadLinkedAccounts() {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '[]');
  } catch { return []; }
}

function saveLinkedAccounts(accounts) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

function getDomainFromEmail(email) {
  if (!email) return null;
  const domain = email.split('@')[1];
  if (!domain || domain === 'jubly.me' || domain === 'jubly.net') return null;
  return domain;
}

function upsertAccount(accounts, user) {
  if (!user?.id) return accounts;
  const email = user.emailAddress || user.email;
  const entry = {
    id: user.id,
    email,
    displayName: user.displayName || user.display_name,
    initial: (user.displayName || email || 'U')[0].toUpperCase(),
    domain: getDomainFromEmail(email),
  };
  // Remove any existing entries with same id OR same email (dedup)
  const filtered = accounts.filter((a) => a.id !== user.id && a.email !== email);
  filtered.push(entry);
  return filtered;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkedAccounts, setLinkedAccounts] = useState(loadLinkedAccounts);

  useEffect(() => {
    getProfile()
      .then((res) => {
        const u = res?.data?.user || res;
        const userData = {
          id: u.id,
          emailAddress: u.email,
          displayName: u.displayName || u.display_name,
          isSuperAdmin: u.isSuperAdmin || false,
          organizations: res?.data?.organizations || [],
        };
        setUser(userData);
        // Add current user to linked accounts
        const updated = upsertAccount(loadLinkedAccounts(), userData);
        saveLinkedAccounts(updated);
        setLinkedAccounts(updated);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await apiLogin(email, password || undefined);
    const u = res?.data?.user || res;
    const userData = {
      id: u.id,
      emailAddress: u.email,
      displayName: u.displayName || u.display_name,
      isSuperAdmin: u.isSuperAdmin || false,
      organizations: res?.data?.organizations || [],
    };
    setUser(userData);
    // Add to linked accounts
    const updated = upsertAccount(loadLinkedAccounts(), userData);
    saveLinkedAccounts(updated);
    setLinkedAccounts(updated);
    return res;
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const switchAccount = useCallback(async (account) => {
    // Always switch via jubly.net login — mail.domain may not be configured yet
    try { await apiLogout(); } catch { /* ignore */ }
    setUser(null);
    window.location.href = `/login?email=${encodeURIComponent(account.email)}&switch=1`;
  }, []);

  const removeLinkedAccount = useCallback((accountId) => {
    const updated = loadLinkedAccounts().filter((a) => a.id !== accountId);
    saveLinkedAccounts(updated);
    setLinkedAccounts(updated);
  }, []);

  const isAuthenticated = !!user;

  // Other accounts (not the current one)
  const otherAccounts = linkedAccounts.filter((a) => a.id !== user?.id);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, isAuthenticated,
      linkedAccounts, otherAccounts, switchAccount, removeLinkedAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default useAuth;
